// RealityAI API client — canonical path: Browser → Nginx /api/* → FastAPI
// AUTH-HARDENING V2 H3: Web uses the trusted cookie surface
// (/api/auth/web/*). The refresh credential is an HttpOnly cookie ONLY —
// JS never sees it. The access JWT lives in memory (auth-memory.ts) and
// is re-minted on reload via /web/access (no rotation). MODEL A:
// bootstrap/new-tab -> /access; expired access (401) -> /web/refresh.

import {
  getMemoryAccess,
  setMemoryAccess,
  withRefreshLockV2,
  broadcastAuthEvent,
  bumpGeneration,
  readGeneration,
  getBroadcastAccess,
  purgeLegacyWebTokens,
} from './auth-memory';

export interface AuthTokens {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
}

export function getToken(): string | null {
  return getMemoryAccess();
}
export function getRefreshToken(): string | null {
  // H3: the refresh credential is HttpOnly-cookie-only — JS cannot read it.
  return null;
}
export function setTokens(t: { access_token: string }) {
  setMemoryAccess(t.access_token);
}
export function clearToken() {
  setMemoryAccess(null);
  broadcastAuthEvent({ type: 'LOGOUT' });
}

const WEB_AUTH_HEADERS: Record<string, string> = {
  'X-RealityAI-Auth': 'web',
};

// One-time migration cleanup: legacy localStorage JWTs must not linger
// after the H3 bundle loads (rollback impact: re-login, by design).
purgeLegacyWebTokens();

export class ApiError extends Error {
  status: number;
  // K7: the full parsed error body (when JSON), so consumers can read
  // structured fields beyond `detail` — e.g. the K5 429 contract's
  // {code, retry_after} (AGENT_API.md §3.6). `message` (detail) is kept
  // as-is for every existing consumer.
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// «Почта не подтверждена» — the temporary verification gate (admin confirms
// the email in RCC). Distinct from a plain 403/401 so the UI can show the
// waiting screen instead of bouncing to the login form.
export const EMAIL_PENDING_MARKER = "Почта не подтверждена";

export function isEmailPendingError(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 403 &&
    e.message.includes(EMAIL_PENDING_MARKER)
  );
}

let refreshInFlight: Promise<boolean> | null = null;

// MODEL A rotation path: expired/invalid access -> POST /web/refresh.
// Cross-tab leader protocol (generation model): the caller's request was
// made with generation G; inside the Web Locks mutex, if a sibling leader
// already advanced the generation, we ADOPT its access (broadcast or
// re-mint via /access) instead of replaying a rotation — the backend
// reuse invariant (DR-030) is never triggered by benign multi-tab races.
export async function tryRefresh(failedGeneration?: number): Promise<boolean> {
  const gen = failedGeneration ?? readGeneration();
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        return await withRefreshLockV2(async () => {
          // A sibling advanced the generation while we waited: adopt.
          if (readGeneration() > gen) {
            if (getMemoryAccess()) return true;
            const adopted = getBroadcastAccess(gen);
            if (adopted) {
              setMemoryAccess(adopted);
              return true;
            }
            // Generation moved but we missed the payload: re-mint from the
            // HttpOnly cookie WITHOUT rotation (safe, no reuse risk).
            // NOTE: already inside the lock — call the unlocked core
            // (nested navigator.locks.request would deadlock this tab).
            return await bootstrapAccessUnlocked();
          }
          // We are the leader: rotate (this is the only rotation path).
          const res = await fetch('/api/auth/web/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...WEB_AUTH_HEADERS },
          });
          if (!res.ok) return false;
          const data = (await res.json()) as AuthTokens;
          const nextGen = bumpGeneration();
          setMemoryAccess(data.access_token);
          broadcastAuthEvent({
            type: 'ACCESS_UPDATED',
            access: data.access_token,
            generation: nextGen,
          });
          return true;
        });
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

// MODEL A bootstrap path: page load / new tab / reload -> POST /web/access.
// Mints an access JWT from the active HttpOnly session cookie WITHOUT
// rotating the refresh family, so concurrent bootstraps are safe by
// construction (no lock needed for correctness; the lock is still taken
// to serialize with a leader rotation and avoid minting against a
// just-consumed session row).
async function bootstrapAccessUnlocked(): Promise<boolean> {
  const res = await fetch('/api/auth/web/access', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...WEB_AUTH_HEADERS },
  });
  if (!res.ok) return false;
  const data = (await res.json()) as AuthTokens;
  setMemoryAccess(data.access_token);
  // Adopt the current generation (bootstrap does not rotate; it does
  // not bump either — siblings' pending waits stay valid).
  return true;
}

export async function bootstrapAccess(): Promise<boolean> {
  return withRefreshLockV2(bootstrapAccessUnlocked);
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
  _retried = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(path, { ...options, headers, credentials: 'same-origin' });

  if (res.status === 401 && !_retried) {
    // Access token may have expired — rotate once (MODEL A), then retry.
    const ok = await tryRefresh(readGeneration());
    if (ok) {
      return request<T>(path, options, true);
    }
    clearToken();
    window.location.reload();
    throw new ApiError(401, 'Сессия истекла. Войдите заново.');
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    let body: unknown;
    try {
      const data = await res.json();
      body = data;
      if (data.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail, body);
  }

  return res.json() as Promise<T>;
}

// ---------- Mobile app (APK) ----------

export interface MobileAppLatest {
  version: string;
  versionCode: number;
  fileName: string;
  size: number;
  md5: string;
  builtAt: string;
  download_path: string;
  download_token: string;
}

/** Newest mobile build + a short-lived (15 min) signed download token. */
export async function fetchMobileLatest(): Promise<MobileAppLatest> {
  return request<MobileAppLatest>('/api/mobile/latest');
}

/** Signed absolute URL for the APK (phone browser download). */
export function mobileApkDownloadUrl(latest: MobileAppLatest): string {
  const base = window.location.origin;
  return `${base}${latest.download_path}?token=${encodeURIComponent(latest.download_token)}`;
}

// ---------- Auth ----------

export interface UserInfo {
  id: string;
  email: string;
  name?: string;
  username?: string;
  full_name?: string;
  role?: string;
  balance?: number;
  tariff_code?: string | null;
  tariff_name?: string | null;
  tariff_ends_at?: string | null;
  // UI/UX Rebuild F2 (decision #2/#3): entitlement-filtered model catalog +
  // display names (decision #11) + active-day status, all from /api/users/me.
  available_models?: { llm: string[]; image: string[]; video: string[] };
  model_display_names?: Record<string, string>;
  active_day_used?: boolean;
  active_days_this_month?: number;
  last_login_at?: string | null;
  // Verification gate: admin confirms the email in RCC before the
  // account may enter the service (login/refresh -> 403 until then).
  email_verified?: boolean;
}

export async function login(email: string, password: string): Promise<AuthTokens> {
  // H3: trusted Web surface route. Backend sets the HttpOnly refresh
  // cookie and returns the access JWT; only the access is kept (memory).
  const res = await fetch('/api/auth/web/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...WEB_AUTH_HEADERS },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    } catch { /* ignore */ }
    throw new ApiError(res.status, detail);
  }
  const data = (await res.json()) as AuthTokens;
  bumpGeneration();
  setMemoryAccess(data.access_token);
  broadcastAuthEvent({ type: 'ACCESS_UPDATED', access: data.access_token, generation: readGeneration() });
  return data;
}

// Derive a valid alnum username from the email / name (backend requires
// username: alnum, 3..50).
function deriveUsername(email: string, name?: string): string {
  const base = (name && name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 30)) || email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
  return (base || 'user' + Math.floor(Math.random() * 1000)).slice(0, 50);
}

export async function register(email: string, password: string, name?: string): Promise<AuthTokens> {
  // Backend UserCreate requires email + username + password + name.
  // H3: registration stays on the shared endpoint (no web surface route
  // for register in H2); the returned refresh token is DISCARDED — the
  // session is then bootstrapped through the cookie surface so the Web
  // client never holds a refresh credential.
  const res = await request<AuthTokens>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, username: deriveUsername(email, name), name: name || email.split('@')[0] }),
  });
  void res.refresh_token; // never stored (H3: refresh is cookie-only)
  // Legacy register issued a bearer pair; convert the session to the
  // cookie surface by logging out of the legacy pair and logging in via
  // the web route (single round-trip, no user-visible step).
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Authorization: `Bearer ${res.access_token}` },
    });
  } catch { /* best effort */ }
  return login(email, password);
}

export async function fetchMe(): Promise<UserInfo> {
  // F2 (decision #2/#3): /api/users/me returns entitlement-filtered
  // available_models, model_display_names and active-day status (unlike /api/me).
  return request<UserInfo>('/api/users/me');
}

// F6 (decision #7): Phase A billing — activate a premium Active Day.
export async function activateActiveDay(
  productCode: string = 'ACTIVE_CHAT',
): Promise<{ status: string; message?: string; window?: unknown }> {
  return request<{ status: string; message?: string; window?: unknown }>(
    `/api/active-day/activate?product_code=${encodeURIComponent(productCode)}`,
    { method: 'POST' },
  );
}

// F6 (decision #7): read-only transaction ledger (USER endpoint).
export interface Transaction {
  id: string;
  user_id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}
export async function fetchTransactions(limit = 50): Promise<Transaction[]> {
  return request<Transaction[]>(`/api/transactions?limit=${limit}`);
}

// F7: self-service profile update (PUT /api/users/me, whitelist username/name).
export async function updateProfile(data: {
  username?: string;
  name?: string;
}): Promise<UserInfo> {
  return request<UserInfo>('/api/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Personal cabinet: authenticated password change (current password required).
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  return request<{ message: string }>('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

// Server-side logout (best-effort) on the trusted web surface: revokes
// the session AND expires the HttpOnly cookie server-side. Always clears
// local memory access and notifies sibling tabs.
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/web/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { ...WEB_AUTH_HEADERS },
    });
  } catch {
    /* ignore — local clear still happens */
  }
  bumpGeneration();
  clearToken();
}

// ---------- Models ----------

// UI/UX Rebuild F1: selector entry derived from /api/users/me
// (entitlement-filtered names + display names from B1).
export interface SelectorModel {
  name: string;
  display: string;
  isFree: boolean;
}

export interface LogicalModel {
  id: number;
  name: string;
  description?: string;
  model_type?: string;
  context_window?: number;
  max_tokens?: number;
  is_free?: boolean;
  is_active?: boolean;
  modality?: string;
  supports_images?: boolean;
  supports_files?: boolean;
}

export async function fetchModels(): Promise<LogicalModel[]> {
  return request<LogicalModel[]>('/api/models');
}

// ---------- Chats ----------

export interface Chat {
  id: string;
  title: string;
  selected_model?: string | null;
  is_pinned?: boolean;
  is_archived?: boolean;
  created_at: string;
  updated_at: string;
  // Projects (Phase 3): null = standalone chat, <uuid> = project chat.
  project_id?: string | null;
  // Project identity (Phase F; null for standalone) — icon/color/marker.
  project_name?: string | null;
  project_icon?: string | null;
  project_color?: string | null;
  show_in_global_chat?: boolean | null;
}

export interface AttachmentPayload {
  kind: 'image' | 'pdf' | 'text' | string;
  name: string;
  mime?: string | null;
  size?: number | null;
  data_url?: string | null;
  text?: string | null;
  file_id?: string;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  model?: string | null;
  attachments?: AttachmentPayload[];
  /** Additive (WG-1 §15): backend-owned citations for web-backed answers. */
  sources?: { title: string; url?: string; domain?: string; sourceId?: string; snippet?: string; publishedAt?: string }[];
}

export async function fetchChats(limit = 100): Promise<Chat[]> {
  // F10 (decision #12): backend defaults to limit=20 — request 100 so the
  // user's history is not silently truncated.
  return request<Chat[]>(`/api/chats?limit=${limit}`);
}

export async function createChat(title?: string, model?: string): Promise<Chat> {
  return request<Chat>('/api/chats', {
    method: 'POST',
    body: JSON.stringify({ title: title || 'New Chat', selected_model: model }),
  });
}

export async function fetchChat(chatId: string): Promise<Chat> {
  return request<Chat>(`/api/chats/${chatId}`);
}

export async function fetchMessages(chatId: string): Promise<ChatMessage[]> {
  return request<ChatMessage[]>(`/api/chats/${chatId}/messages`);
}

export async function deleteChat(chatId: string): Promise<void> {
  return request(`/api/chats/${chatId}`, { method: 'DELETE' });
}

export async function updateChat(
  chatId: string,
  data: { title?: string; is_pinned?: boolean; is_archived?: boolean },
): Promise<Chat> {
  return request<Chat>(`/api/chats/${chatId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ---------- User file library (50MB quota per account) ----------

export interface UserFileItem {
  id: string;
  name: string;
  mime?: string | null;
  size: number;
  kind: string;
  created_at: string;
}

export interface UserFilesResponse {
  files: UserFileItem[];
  quota_used: number;
  quota_limit: number;
}

export async function uploadFile(file: File): Promise<UserFileItem & { quota_used: number; quota_limit: number }> {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/files', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    } catch { /* ignore */ }
    throw new ApiError(res.status, detail);
  }
  return res.json() as Promise<UserFileItem & { quota_used: number; quota_limit: number }>;
}

export async function fetchFiles(): Promise<UserFilesResponse> {
  return request<UserFilesResponse>('/api/files');
}

export async function deleteFile(fileId: string): Promise<void> {
  return request(`/api/files/${fileId}`, { method: 'DELETE' });
}

export function fileDownloadUrl(fileId: string): string {
  return `/api/files/${fileId}/download`;
}

// ---------- Support reports (personal cabinet -> RCC) ----------

export interface ReportItem {
  id: string;
  category: string;
  subject: string;
  description: string;
  contact?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export async function createReport(data: {
  category: string;
  subject: string;
  description: string;
  contact?: string;
}): Promise<ReportItem> {
  return request<ReportItem>('/api/reports', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchMyReports(): Promise<ReportItem[]> {
  return request<ReportItem[]>('/api/reports/mine');
}

// ---------- WG-3.1 Smart Search (web access mode) ----------

export type WebSearchMode = "off" | "auto" | "on";

export interface UserPreferences {
  realityai_guide_enabled: boolean;
  web_search_mode: WebSearchMode;
}

export async function getPreferences(): Promise<UserPreferences> {
  return request<UserPreferences>("/api/users/preferences");
}

export async function putPreferences(
  patch: Partial<Pick<UserPreferences, "web_search_mode" | "realityai_guide_enabled">>,
): Promise<UserPreferences> {
  return request<UserPreferences>("/api/users/preferences", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

// ---------- Streaming (SSE over fetch ReadableStream) ----------

export interface StreamEvent {
  type: 'message_start' | 'delta' | 'citations' | 'web_status' | 'usage' | 'done' | 'error';
  data: any;
}

export interface WebStatusPayload {
  status: "enabled" | "degraded" | "unavailable" | "off";
  count?: number;
  notice?: string;
  // WG-4B §56: which pipeline produced this status (additive).
  operation?: "search" | "reader" | "reader_find";
}

export interface StreamCallbacks {
  onMessageStart?: (data: any) => void;
  onDelta?: (text: string, data: any) => void;
  onCitations?: (items: any[]) => void;
  onWebStatus?: (data: WebStatusPayload) => void;
  onUsage?: (data: any) => void;
  onDone?: (data: any) => void;
  onError?: (data: any) => void;
}

export async function streamMessage(
  chatId: string,
  content: string,
  model: string | undefined,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
  attachments?: AttachmentPayload[],
  webMode?: WebSearchMode,
): Promise<void> {
  const token = getToken();
  const body = JSON.stringify({
    content,
    model,
    attachments: attachments ?? [],
    // WG-3.1: additive web.mode — the server treats absence as AUTO.
    ...(webMode ? { web: { mode: webMode } } : {}),
  });
  const res = await fetch(`/api/chats/${chatId}/messages/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  });

  if (res.status === 401) {
    const ok = await tryRefresh();
    if (ok) {
      const newToken = getToken();
      const retry = await fetch(`/api/chats/${chatId}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
      },
      body,
      signal,
      });
      return readStream(retry, callbacks);
    }
    clearToken();
    window.location.reload();
    throw new ApiError(401, 'Сессия истекла. Войдите заново.');
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }

  if (!res.body) {
    throw new ApiError(500, 'Пустой ответ сервера');
  }

  await readStream(res, callbacks);
}

async function readStream(res: Response, callbacks: StreamCallbacks): Promise<void> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';

    for (const frame of frames) {
      const eventLine = frame.split('\n').find((l) => l.startsWith('event: '));
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue;

      const eventType = eventLine ? eventLine.slice(7).trim() : 'message';
      const raw = dataLine.slice(6).trim();

      // OpenAI-style chunk stream (data: {...} without event lines)
      if (!eventLine) {
        if (raw === '[DONE]') {
          callbacks.onDone?.({ finish_reason: 'stop' });
          continue;
        }
        try {
          const chunk = JSON.parse(raw);
          const deltaText = chunk.choices?.[0]?.delta?.content;
          if (deltaText) callbacks.onDelta?.(deltaText, chunk);
          if (chunk.usage) callbacks.onUsage?.(chunk.usage);
        } catch {
          /* skip non-JSON */
        }
        continue;
      }

      let data: any = null;
      try {
        data = JSON.parse(raw);
      } catch {
        data = { text: raw };
      }

      switch (eventType) {
        case 'message_start':
          callbacks.onMessageStart?.(data);
          break;
        case 'delta':
          if (data?.text) callbacks.onDelta?.(data.text, data);
          break;
        case 'citations':
          callbacks.onCitations?.(data?.items || data || []);
          break;
        case 'web_status':
          callbacks.onWebStatus?.(data);
          break;
        case 'usage':
          callbacks.onUsage?.(data);
          break;
        case 'done':
          callbacks.onDone?.(data);
          break;
        case 'error':
          callbacks.onError?.(data);
          break;
      }
    }
  }
}